"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { Check, X } from "lucide-react"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import type { CommunityPost } from "@/lib/community/types"
import { ABILITY_TYPE_CONFIG } from "@/lib/community/types"
import { AbilityTypeBadge } from "./ability-type-badge"

interface CopyToAccountPanelProps {
  post: CommunityPost
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

/**
 * CopyToAccountPanel — centered glass modal (same pattern as LegalModal).
 * Shows a preview of the ability being copied, with confirm/cancel actions.
 */
export function CopyToAccountPanel({
  post,
  isOpen,
  onClose,
  onConfirm,
}: CopyToAccountPanelProps) {
  const { closing, requestClose } = useAnimatedClose(onClose, 280)
  const [entered, setEntered] = useState(false)
  const [copying, setCopying] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setEntered(false)
      const raf = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(raf)
    }
  }, [isOpen])

  const handleConfirm = async () => {
    if (copying || copied) return
    setCopying(true)
    try {
      await onConfirm()
    } finally {
      setCopying(false)
      setCopied(true)
      setTimeout(() => {
        setCopied(false)
        requestClose()
      }, 1500)
    }
  }

  const panelStyle = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing
      ? "scale(0.96) translateY(8px)"
      : entered
        ? "scale(1) translateY(0)"
        : "scale(0.92) translateY(12px)",
    filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
    transition: "opacity 280ms, transform 280ms, filter 280ms",
    transitionTimingFunction: closing
      ? "cubic-bezier(0.4, 0, 1, 1)"
      : "cubic-bezier(0.16, 1, 0.3, 1)",
  }

  if (!isOpen) return null

  const typeConfig = ABILITY_TYPE_CONFIG[post.abilityType]

  return createPortal(
    <>
      {/* Overlay backdrop */}
      <div
        className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${
          closing
            ? "opacity-0 backdrop-blur-none"
            : entered
              ? "opacity-100 backdrop-blur-xl"
              : "opacity-0 backdrop-blur-none"
        }`}
        style={{ zIndex: Z.MODAL_NESTED, pointerEvents: closing || !entered ? "none" : "auto" }}
        onClick={requestClose}
        aria-hidden
      />

      {/* Centered modal container */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_NESTED + 1, pointerEvents: "none" }}
      >
        <div
          className="w-full max-w-md"
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          <div className="liquid-glass-modal rounded-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h2 className="text-base font-semibold text-foreground">
                加入我的{typeConfig.labelZh}库
              </h2>
              <button
                onClick={requestClose}
                className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              {copied ? (
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className={`flex items-center justify-center w-14 h-14 rounded-full ${ABILITY_TYPE_CONFIG[post.abilityType].joinDoneBgClass}`}>
                    <Check size={28} strokeWidth={2.5} />
                  </div>
                  <p className={`text-base font-medium ${ABILITY_TYPE_CONFIG[post.abilityType].outlineTextClass}`}>
                    已加入{typeConfig.labelZh}库！
                  </p>
                </div>
              ) : (
                <>
                  {/* Ability preview */}
                  <div className="liquid-glass-surface rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate mb-1.5">
                          {post.title}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <AbilityTypeBadge type={post.abilityType} />
                          <span className="text-xs text-muted-foreground">
                            by {post.author.displayName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground text-center mb-2">
                    此操作将把该{typeConfig.labelZh}添加到你的能力库
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            {!copied && (
              <div className="px-6 py-4 shrink-0">
                <button
                  onClick={handleConfirm}
                  disabled={copying}
                  className={[
                    "w-full h-9 flex items-center justify-center gap-2",
                    `rounded-xl ${ABILITY_TYPE_CONFIG[post.abilityType].joinBgClass}`,
                    "text-white text-sm font-semibold",
                    "transition-colors duration-150",
                    copying ? "opacity-70 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  {copying ? (
                    <>
                      <span
                        className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                        aria-hidden="true"
                      />
                      加入中…
                    </>
                  ) : (
                    "确认"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
